// import "antd/es/date-picker/style/index";

import { Col, Row } from "antd";
import generatePicker from "antd/es/date-picker/generatePicker";
import dateFnsGenerateConfig from "rc-picker/lib/generate/dateFns";
import type { FC } from "react";

const DatePicker = generatePicker<Date>(dateFnsGenerateConfig);
type DatePickerProps = typeof DatePicker["defaultProps"];

type StepDatePickerProps = DatePickerProps & {
  inputs?: "single" | "double";
  onDateChange?: (value: Date | null) => void;
  onEndDateChange?: (value: Date | null) => void;
  disabledStartDate?: (value: Date) => boolean;
  disabledEndDate?: (value: Date) => boolean;
  endDatePlaceholder?: string;
  date?: Date;
  endDate?: Date;
};

/**
 * Component that is meant to be used as a single input picker for dates or as a range of dates
 * NOTE: antd has a range picker but it's a single input instead of two separated ones
 */
export const StepDatePicker: FC<StepDatePickerProps> = ({
  placeholder,
  inputs = "single",
  date: startDate,
  onDateChange,
  disabledStartDate,
  disabledEndDate,
  onEndDateChange,
  endDatePlaceholder,
  endDate,
  disabled,
}) => {
  const onDisabledStartDate = (current: Date): boolean => {
    const formattedEndDate = endDate ? new Date(endDate) : undefined;
    return (
      (!!isDateTomorrowOrLater && isDateTomorrowOrLater(current)) ||
      (!!formattedEndDate && current >= formattedEndDate) ||
      (!!disabledStartDate && disabledStartDate(current))
    );
  };

  const onDisabledEndDate = (current: Date): boolean => {
    const currentDate = new Date(current);
    const formattedStartDate = startDate ? new Date(startDate) : undefined;
    return (
      (!!isDateTomorrowOrLater && isDateTomorrowOrLater(current)) ||
      (!!formattedStartDate && currentDate <= formattedStartDate) ||
      (!!disabledEndDate && disabledEndDate(current))
    );
  };

  const isDateTomorrowOrLater = (current: Date) => {
    const currentDate = new Date(current);
    const tomorrowDate = new Date();
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    return currentDate >= tomorrowDate;
  };

  return (
    <Row gutter={[10, 16]} justify="space-between" className="inputs-container">
      <Col xl={inputs === "single" ? 24 : 12} xs={24}>
        <DatePicker
          className="date-picker"
          size="large"
          placeholder={placeholder}
          placement="bottomRight"
          allowClear
          format={"YYYY/MM/DD"}
          value={startDate}
          showToday={false}
          disabled={disabled}
          disabledDate={onDisabledStartDate}
          onChange={onDateChange}
        />
      </Col>
      {inputs === "double" && (
        <Col xl={12} xs={24}>
          <DatePicker
            className="date-picker"
            size="large"
            placeholder={endDatePlaceholder}
            placement="bottomRight"
            allowClear
            format={"YYYY/MM/DD"}
            showToday={false}
            disabled={disabled}
            value={endDate ? endDate : null}
            disabledDate={onDisabledEndDate}
            onChange={onEndDateChange}
          />
        </Col>
      )}
    </Row>
  );
};
